<script setup lang="ts">
import type SelectCountry from '@/components/base/form/SelectCountry.vue';
import IconWrapper from '@/components/base/IconWrapper.vue';

type Props = {
  opacity?: boolean;
  allowGrow?: boolean;
  // rótulo alternativo do botão de cancelar edição (Minha Conta V2 usa "Voltar")
  cancelLabel?: string;
};

const {
  opacity = false,
  allowGrow = false,
  cancelLabel = '',
} = defineProps<Props>();

const { t } = useTexts();
const { openToaster } = useLayoutStore();

const {
  userId,
  userCountry,
  userCurrency,
  userLanguage,
  userTimezone,
  userUsername,
  fetchUserProfile,
} = useUserData();

const { visitorOrUserTimezone } = useParameters();
const { walletHasFirstDeposit, walletIsLoading } = useWallet();
const { updateUserAccount } = useUserDataStore();

const availableLocales = [{ code: 'pt-br', name: 'Português' }];

const getLocaleImage = (locale: string) => {
  return getAssetsUrlForCDN(`/assets/images/langs/${locale}.png`);
};

const isLoading = ref<boolean>(false);
const isEditing = ref<boolean>(false);

const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

const selectedLanguage = ref<string>('');

function fillDefaultSelectedLanguageValue() {
  if (!userLanguage?.value) {
    return;
  }

  selectedLanguage.value = userLanguage.value;
}

const handdleRefreshUserProfile = () => {
  isLoading.value = true;

  fetchUserProfile({ force: true }).finally(() => {
    isLoading.value = false;
    isEditing.value = false;
  });
};

const isButtonChangeLanguageEnabled = computed(() => {
  return selectedLanguage.value !== userLanguage.value;
});

async function handdleChangeLanguage() {
  if (!userId.value || !isButtonChangeLanguageEnabled.value) {
    return;
  }

  isLoading.value = true;

  const payload = {
    language: selectedLanguage.value,
    captcha_token: '',
  };

  await updateUserAccount(userId.value, payload)
    .then(() => {
      handdleRefreshUserProfile();
      openToaster({
        message: t('account.user_data.update_language_success'),
        type: 'success',
      });
    })
    .catch(() => {
      isLoading.value = false;

      openToaster({
        message: t('account.user_data.errors.update_error'),
        type: 'error',
      });
    });
}

const selectCountryRef = ref<InstanceType<typeof SelectCountry> | null>(null);

const finalSelectedCountry = computed<string>(() => {
  return selectCountryRef.value?.selectedCountry ?? '';
});

const userCanChangeCountry = computed(() => {
  return (
    !walletHasFirstDeposit.value &&
    !walletIsLoading.value &&
    selectCountryRef.value?.availableCountries &&
    selectCountryRef.value?.availableCountries?.length >= 1
  );
});

const isButtonChangeCountryEnabled = computed(() => {
  return finalSelectedCountry.value !== userCountry.value;
});

async function handdleChangeCountry() {
  if (!userId.value || !isButtonChangeCountryEnabled.value) {
    return;
  }

  isLoading.value = true;

  const payload = {
    country: finalSelectedCountry.value,
    captcha_token: '',
  };

  await updateUserAccount(userId.value, payload)
    .then(() => {
      handdleRefreshUserProfile();
      openToaster({
        message: t('account.user_data.update_country_success'),
        type: 'success',
      });
    })
    .catch(() => {
      isLoading.value = false;
      openToaster({
        message: t('account.user_data.errors.update_error'),
        type: 'error',
      });
    });
}

async function updateUserTimezone() {
  if (!userId.value) {
    return;
  }

  const payload = {
    timezone: visitorOrUserTimezone.value,
    captcha_token: '',
  };

  isLoading.value = true;

  await updateUserAccount(userId.value, payload).then(() => {
    fetchUserProfile({ force: true }).finally(() => {
      isLoading.value = false;
    });
  });
}

onBeforeMount(() => {
  if (!userCountry.value) {
    handdleRefreshUserProfile();
  }
});

onMounted(() => {
  fillDefaultSelectedLanguageValue();

  if (!userTimezone.value) {
    updateUserTimezone();
  }
});

onActivated(() => {
  fillDefaultSelectedLanguageValue();
});

watch(
  () => userLanguage.value,
  () => {
    fillDefaultSelectedLanguageValue();
  },
);

const hasPendency = ref<boolean>(false);

defineExpose({
  name: 'infos',
  isEditing,
  hasPendency,
  toggleEdit,
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div
      :class="[
        $style.container,
        { [$style.opacity]: opacity, [$style.grow]: allowGrow && !isEditing },
      ]"
    >
      <UserProfileDataCard
        :action-label="
          isEditing
            ? cancelLabel || $t('general.cancel')
            : $t('general.see_data')
        "
        :action-type="!isEditing ? 'view' : undefined"
        :label="$t('account.infos_section.account_data')"
        @action="toggleEdit"
      >
        <div class="flex gap-[5px] uppercase">
          <div class="inline-flex items-center gap-2">
            <IconWrapper
              v-if="userCountry"
              class="inline-flex"
              :name="`flags/${userCountry}`"
            />
            {{ userCountry ? `${userCountry}` : '' }}
          </div>
          <span
            v-if="userCurrency"
            class="opacity-30"
          >
            |
          </span>
          {{ userCurrency }}
          <span
            v-if="userLanguage"
            class="opacity-30"
          >
            |
          </span>
          {{ userLanguage }}
        </div>
      </UserProfileDataCard>

      <div
        v-if="isEditing"
        :class="$style.editContent"
      >
        <div :class="$style.description">
          {{ $t('account.infos_section.details', { appName: appName() }) }}
        </div>

        <div :class="$style.formContainer">
          <div
            ref="country"
            :class="[$style.formGroup, 'md:col-span-2']"
          >
            <label for="userCountry">
              {{ $t('account.user_data.country') }}
            </label>

            <div
              :class="[
                $style.editableField,
                { [$style.isDisabled]: !userCanChangeCountry },
              ]"
            >
              <div class="relative inline-block text-left">
                <BaseFormSelectCountry
                  ref="selectCountryRef"
                  :show-label="false"
                  :user-can-change-country="userCanChangeCountry"
                />
              </div>

              <button
                v-if="userCanChangeCountry"
                :class="[
                  $style.saveButton,
                  { [$style.isDisabled]: !isButtonChangeCountryEnabled },
                ]"
                @click.prevent="handdleChangeCountry"
              >
                {{ $t('account.user_data.change_country') }}
              </button>
            </div>
          </div>

          <div :class="[$style.formGroup, 'md:col-span-2']">
            <label for="userCurrency">
              {{ $t('account.user_data.currency') }}
            </label>

            <BaseFormInput
              id="userCurrency"
              :key="userCurrency"
              autocomplete="off"
              disabled
              :initial-value="userCurrency"
              :model-value="userCurrency"
              name="userCurrency"
              :placeholder="$t('account.user_data.currency')"
              type="text"
            />
          </div>

          <div :class="[$style.formGroup, 'md:col-span-2']">
            <label for="userLang">
              {{ $t('account.user_data.language') }}
            </label>

            <div :class="$style.editableField">
              <HeadlessListbox
                v-model="selectedLanguage"
                as="div"
                class="relative inline-block text-left w-full"
              >
                <HeadlessListboxButton
                  id="userLang"
                  class="inline-flex w-full items-center rounded-none px-4 py-2 text-sm font-medium"
                >
                  <BaseCommonCustomNuxtImg
                    :alt="appName()"
                    class="mr-2 w-4"
                    :src="getLocaleImage(selectedLanguage)"
                  >
                    <template #fallback>
                      <img
                        :alt="appName()"
                        :src="getLocaleImage(`xx`)"
                      />
                    </template>
                  </BaseCommonCustomNuxtImg>
                  {{ selectedLanguage || $t('account.user_data.language') }}
                  <NuxtIcon
                    class="ml-3 opacity-30"
                    name="arrows/angle_down"
                  />
                </HeadlessListboxButton>
                <HeadlessListboxOptions
                  class="absolute right-50 mt-3 w-32 origin-top-right divide-y divide-gray-100 rounded-md bg-auth-bg-inputs shadow-lg ring-1 ring-black/5 focus:outline-none z-[999] overflow-auto max-h-[205px] p-1"
                >
                  <HeadlessListboxOption
                    v-for="option in availableLocales"
                    :key="option.id"
                    :value="option.code"
                  >
                    <span
                      class="group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium cursor-pointer"
                    >
                      <BaseCommonCustomNuxtImg
                        :alt="appName()"
                        class="mr-2 w-4"
                        :src="getLocaleImage(option.code)"
                      >
                        <template #fallback>
                          <img
                            :alt="appName()"
                            class="mr-2 w-4"
                            :src="getLocaleImage(`xx`)"
                          />
                        </template>
                      </BaseCommonCustomNuxtImg>
                      {{ option.code }}
                    </span>
                  </HeadlessListboxOption>
                </HeadlessListboxOptions>
              </HeadlessListbox>

              <button
                :class="[
                  $style.saveButton,
                  { [$style.isDisabled]: !isButtonChangeLanguageEnabled },
                ]"
                @click.prevent="handdleChangeLanguage"
              >
                {{ $t('account.user_data.change_language') }}
              </button>
            </div>
          </div>

          <div :class="$style.formGroup">
            <label for="userTimeZone">
              {{ $t('account.user_data.time_zone') }}
            </label>

            <BaseFormInput
              id="userTimeZone"
              :key="userTimezone"
              autocomplete="off"
              disabled
              :initial-value="userTimezone"
              :model-value="userTimezone"
              name="userTimeZone"
              :placeholder="$t('account.user_data.time_zone')"
              type="text"
            />
          </div>

          <div :class="$style.formGroup">
            <label for="userid">
              {{ $t('account.userId') }}
            </label>

            <BaseFormInput
              id="userid"
              disabled
              :initial-value="userUsername"
              :model-value="userUsername"
              name="userid"
              :placeholder="$t('account.userId')"
              type="text"
            />
          </div>
        </div>

        <div
          v-if="userCanChangeCountry"
          class="alert alert-warning mt-2"
        >
          {{ $t('account.user_data.change_country_message') }}
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.container {
  @apply flex flex-col gap-3;
}

.grow {
  --card-grow: 1;
}

.opacity {
  @apply opacity-20 pointer-events-none select-none;
}

.editContent {
  @apply w-full;

  .description {
    @apply text-sm md:text-sm text-texts/70 mb-4 max-w-[450px];
  }

  .formContainer {
    @apply w-full md:grid md:gap-x-4 gap-y-1 md:grid-cols-2;

    .formGroup {
      @apply mb-4 relative;

      label {
        @apply text-xs font-medium;
      }
    }

    .editableField {
      @apply bg-auth-bg-inputs h-[46px] sm:h-[50px] rounded-md flex items-center relative;

      &.isDisabled * {
        @apply cursor-not-allowed text-texts/40;
      }

      .saveButton {
        @apply absolute right-4 text-[.6rem] flex justify-center items-center gap-1 rounded px-2 h-5 text-nowrap flex-nowrap whitespace-nowrap bg-button-bg text-button-text;

        &.isDisabled {
          @apply opacity-30 cursor-not-allowed;
        }
      }
    }
  }
}
</style>
